"use client"

import { useState, useMemo } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { X, Plus, Minus } from "@/lib/icons"
import { useTranslations } from "next-intl"

interface TagSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  tags: string[]
  onConfirm: (includeTags: string[], excludeTags: string[]) => void
}

type TagState = "none" | "include" | "exclude"

interface TagSelection {
  tag: string
  state: TagState
}

export function TagSelectionModal({ isOpen, onClose, tags, onConfirm }: TagSelectionModalProps) {
  const t = useTranslations("tagSelectionModal")
  const [tagStates, setTagStates] = useState<Map<string, TagState>>(new Map())

  // Group tags by type
  const groupedTags = useMemo(() => {
    const groups: Record<string, string[]> = {
      artist: [],
      character: [],
      series: [],
      group: [],
      type: [],
      language: [],
      male: [],
      female: [],
      tag: [],
    }

    tags.forEach((tag) => {
      const colonIndex = tag.indexOf(":")
      if (colonIndex > 0) {
        const type = tag.substring(0, colonIndex)
        if (type in groups) {
          groups[type].push(tag)
        } else {
          groups.tag.push(tag)
        }
      } else {
        groups.tag.push(tag)
      }
    })

    // Remove empty groups
    return Object.entries(groups).filter(([_, tags]) => tags.length > 0)
  }, [tags])

  const getTagState = (tag: string): TagState => {
    return tagStates.get(tag) || "none"
  }

  const cycleTagState = (tag: string) => {
    const currentState = getTagState(tag)
    const nextState: TagState = currentState === "none" ? "include" : currentState === "include" ? "exclude" : "none"
    
    setTagStates((prev) => {
      const newMap = new Map(prev)
      if (nextState === "none") {
        newMap.delete(tag)
      } else {
        newMap.set(tag, nextState)
      }
      return newMap
    })
  }

  const handleConfirm = () => {
    const includeTags: string[] = []
    const excludeTags: string[] = []

    tagStates.forEach((state, tag) => {
      if (state === "include") {
        includeTags.push(tag)
      } else if (state === "exclude") {
        excludeTags.push(tag)
      }
    })

    onConfirm(includeTags, excludeTags)
  }

  const handleClear = () => {
    setTagStates(new Map())
  }

  const getTagColor = (type: string): string => {
    const colors: Record<string, string> = {
      artist: "bg-purple-500/20 text-purple-300 border-purple-500/30",
      character: "bg-blue-500/20 text-blue-300 border-blue-500/30",
      series: "bg-orange-500/20 text-orange-300 border-orange-500/30",
      group: "bg-pink-500/20 text-pink-300 border-pink-500/30",
      type: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
      language: "bg-green-500/20 text-green-300 border-green-500/30",
      male: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
      female: "bg-rose-500/20 text-rose-300 border-rose-500/30",
      tag: "bg-gray-500/20 text-gray-300 border-gray-500/30",
    }
    return colors[type] || colors.tag
  }

  const getGroupLabel = (type: string): string => {
    const labels: Record<string, string> = {
      artist: "Artists",
      character: "Characters",
      series: "Series/Parody",
      group: "Groups",
      type: "Type",
      language: "Language",
      male: "Male Tags",
      female: "Female Tags",
      tag: "General Tags",
    }
    return labels[type] || type
  }

  const selectedCount = Array.from(tagStates.values()).filter((s) => s !== "none").length

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("selectTags")}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {t("clickOnceToIncludeTwiceToExclude")}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {groupedTags.map(([type, typeTags]) => (
            <div key={type} className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wide">
                {getGroupLabel(type)}
              </h3>
              <div className="flex flex-wrap gap-2">
                {typeTags.map((tag) => {
                  const state = getTagState(tag)
                  const baseColor = getTagColor(type)
                  
                  return (
                    <button
                      key={tag}
                      onClick={() => cycleTagState(tag)}
                      className={cn(
                        "relative px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                        "border hover:scale-105",
                        baseColor,
                        state === "include" && "ring-2 ring-green-500 bg-green-500/30",
                        state === "exclude" && "ring-2 ring-red-500 bg-red-500/30"
                      )}
                    >
                      {state === "include" && (
                        <Plus className="inline-block w-3 h-3 mr-1 text-green-400" />
                      )}
                      {state === "exclude" && (
                        <Minus className="inline-block w-3 h-3 mr-1 text-red-400" />
                      )}
                      {tag}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t("tagsSelected", { count: selectedCount })}
            </span>
            {selectedCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClear}>
                {t("clearAll")}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button onClick={handleConfirm} disabled={selectedCount === 0}>
              {t("searchSelectedCount", { selectedCount })}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
